import React, { useState, useEffect, useRef } from "react";
import { Volume2, Loader2, Play, Sparkles } from "lucide-react";
import { getProfile, updateProfile, speak } from "../../lib/api";
import { toast } from "sonner";

const VOICES = [
  { id: "nova", label: "Nova", desc: "Warm & bright (recommended)" },
  { id: "shimmer", label: "Shimmer", desc: "Soft & gentle" },
  { id: "coral", label: "Coral", desc: "Expressive & lively" },
  { id: "sage", label: "Sage", desc: "Calm & composed" },
  { id: "fable", label: "Fable", desc: "Storyteller" },
  { id: "alloy", label: "Alloy", desc: "Neutral" },
];

export default function SignatureVoice() {
  const [voice, setVoice] = useState("nova");
  const [speed, setSpeed] = useState(1.0);
  const [autoGreet, setAutoGreet] = useState(true);
  const [previewing, setPreviewing] = useState("");
  const audioRef = useRef(null);

  useEffect(() => {
    getProfile().then((p) => {
      if (p.voice) setVoice(p.voice);
      if (p.voice_speed) setSpeed(p.voice_speed);
      if (typeof p.auto_greet === "boolean") setAutoGreet(p.auto_greet);
    }).catch(() => {});
  }, []);

  const save = async (patch) => {
    try { await updateProfile(patch); } catch (e) {}
  };

  const preview = async (v) => {
    setPreviewing(v);
    try {
      const audio = await speak("Good morning. I'm Aria, your Chief of Staff. I've prepared your day.", v, speed);
      audioRef.current.src = audio;
      audioRef.current.onended = () => setPreviewing("");
      await audioRef.current.play();
    } catch (e) { setPreviewing(""); }
  };

  return (
    <div className="glass-card p-6 sm:p-8 mb-8 fade-up" data-testid="signature-voice">
      <audio ref={audioRef} className="hidden" />
      <div className="flex items-center gap-2 mb-1">
        <Volume2 size={16} className="text-amber-400/80" />
        <span className="overline">Signature Voice</span>
      </div>
      <h3 className="font-display text-2xl font-medium text-amber-50/95 mb-1">How Aria sounds to you</h3>
      <p className="text-slate-400/90 text-sm font-light mb-5">Choose her voice and pace. She'll use it whenever she reads anything aloud.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {VOICES.map((v) => (
          <button key={v.id} onClick={() => { setVoice(v.id); save({ voice: v.id }); }}
            className={`text-left p-4 rounded-xl border transition ${voice === v.id ? "border-amber-500/50 bg-amber-500/10" : "border-white/8 hover:border-amber-500/25"}`}
            data-testid={`voice-${v.id}`}>
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm">{v.label}</span>
              <span onClick={(e) => { e.stopPropagation(); preview(v.id); }} className="p-1.5 rounded-lg hover:bg-amber-500/15 text-amber-300/80 cursor-pointer" data-testid={`voice-preview-${v.id}`}>
                {previewing === v.id ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
              </span>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">{v.desc}</div>
          </button>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-6">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center justify-between text-[12px] text-slate-400 mb-1.5">
            <span>Speaking pace</span><span className="font-mono text-amber-300/80">{speed.toFixed(2)}x</span>
          </div>
          <input type="range" min="0.8" max="1.2" step="0.05" value={speed}
            onChange={(e) => setSpeed(parseFloat(e.target.value))}
            onMouseUp={() => save({ voice_speed: speed })}
            onTouchEnd={() => save({ voice_speed: speed })}
            className="w-full accent-amber-400" data-testid="voice-speed" />
        </div>
        <button onClick={() => { const n = !autoGreet; setAutoGreet(n); save({ auto_greet: n }); toast.success(n ? "Aria will greet you aloud each morning" : "Morning greeting off"); }}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition text-sm ${autoGreet ? "border-amber-500/50 bg-amber-500/10 text-amber-200" : "border-white/10 text-slate-400"}`}
          data-testid="auto-greet-toggle">
          <Sparkles size={15} /> Morning greeting {autoGreet ? "on" : "off"}
        </button>
      </div>
    </div>
  );
}
